import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { Theme } from '@mui/material/styles';
import { MyScheduleSetting } from '../../types';
import { useAuthenticator } from '../../../../../../auth/AuthenticationContext';
import { Fahrenheit } from '../../../../../../system/models/temperatureUnits';
import { Stack } from '@mui/material';
import { DisplayValue } from '../../../../shared-ui';
import { SetpointLimitSettings } from '../../../../../units/types/setpointLimitSettings';

interface EditSettingsProps {
  id?: string;
  templateValues?: boolean;
  mode: 'heating' | 'cooling';
  open: boolean;
  handleChange: (mode: string, value: number) => void;
  value?: number;
  anchorEl: HTMLButtonElement | null;
  theme: Theme;
  handleClick: (
    mode: string,
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => void;
  handleClose: (mode: string, setting: MyScheduleSetting) => void;
  maxCoolSetpointLimit?: SetpointLimitSettings['maxCoolSetpointLimit'];
  maxHeatSetpointLimit?: SetpointLimitSettings['maxHeatSetpointLimit'];
  minCoolSetpointLimit?: SetpointLimitSettings['minCoolSetpointLimit'];
  minHeatSetpointLimit?: SetpointLimitSettings['minHeatSetpointLimit'];
}

export function EditSettings(props: EditSettingsProps) {
  const {
    id,
    templateValues = false,
    mode,
    open,
    anchorEl,
    theme,
    handleClick,
    value,
    handleChange,
    handleClose,
    maxCoolSetpointLimit,
    maxHeatSetpointLimit,
    minCoolSetpointLimit,
    minHeatSetpointLimit,
  } = props;
  const label = () => mode.split('ing')[0] + ' to';
  const key = mode === 'cooling' ? 'maximum' : 'minimum';

  const { user } = useAuthenticator();

  const preferredUnits = user?.preferredUnits ?? Fahrenheit;

  /***
     Thermostat setpoint profile limit settings are checked 
     And constrain the slider max and min values 
     If setpoint profile limits are applied
  ***/

  const sliderMinVal =
    mode === 'cooling' ? minCoolSetpointLimit : minHeatSetpointLimit;
  const sliderMaxVal =
    mode === 'cooling' ? maxCoolSetpointLimit : maxHeatSetpointLimit;
  const available =
    templateValues || (sliderMinVal != null && sliderMaxVal != null);

  return (
    <>
      <Button
        disabled={!available}
        onClick={(event) => handleClick(mode, event)}
        sx={{
          px: 1,
          color: theme.embue[mode],
        }}
      >
        <DisplayValue
          value={value}
          label={label()}
          units={preferredUnits}
          size="h4"
          unitsSize="10px"
        />
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={() => handleClose(mode, { [key]: value })}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        sx={{
          width: 400,
          height: 200,
        }}
      >
        <Box sx={{ px: 2, py: 1, width: 250, height: 100 }}>
          <Typography variant={'button'} gutterBottom>
            {label()}:
          </Typography>
          <Stack spacing={2.5} direction="row" sx={{ marginTop: '10px' }}>
            <DisplayValue
              value={sliderMinVal}
              units={preferredUnits}
              size="h5"
              unitsSize="10px"
            />
            <TextField
              type="number"
              label={`${mode} target (°${preferredUnits})`}
              value={value ?? ''}
              inputProps={{
                min: sliderMinVal ?? undefined,
                max: sliderMaxVal ?? undefined,
                step: 'any',
              }}
              helperText={
                templateValues
                  ? 'Template target; equipment compatibility is checked on assignment.'
                  : undefined
              }
              onChange={(event) => {
                if (!event.target.value.trim()) return;
                const next = Number(event.target.value);
                if (
                  Number.isFinite(next) &&
                  (sliderMinVal == null || next >= sliderMinVal) &&
                  (sliderMaxVal == null || next <= sliderMaxVal)
                )
                  handleChange(mode, next);
              }}
            />
            <DisplayValue
              value={sliderMaxVal}
              units={preferredUnits}
              size="h5"
              unitsSize="10px"
            />
          </Stack>
        </Box>
      </Popover>
    </>
  );
}
